/* eslint-disable camelcase */
import { Dropdown } from 'react-bootstrap';
import { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import toast, { Toaster } from 'react-hot-toast';
import { animateScroll as scroll } from 'react-scroll';

import {
  cloudRoles,
  GUEST_USER_PERMISSIONS,
  roles,
  ROLE_ADMIN,
  ROLE_GUEST,
  ACCESS_TYPE_ALL,
  GUEST_USER_PERMISSIONS_KEYS,
  ROLE_PUBLISHER,
  ALL_USERS_PERMISSIONS,
  ALL_USERS_PERMISSIONS_KEYS,
  ROLE_CREATOR
} from '../../../config/roles';
import { useLocales } from '../../../locales';
import { Controller, useForm } from 'react-hook-form';
import { dispatch } from '../../../redux/store';
import {
  createInvite,
  updateInvite
} from '../../../redux/slices/accounts/invites/invites';
import {
  getTeamMemeberDetails,
  updateGuestUser
} from '../../../redux/slices/accounts/team/team';
import { useNavigate } from 'react-router-dom';
import { PATH_PAGE } from '../../../routes/paths';
import PermissionsRadioInputs from './permissionsRadioInputs';
import {
  fetchSubscriptionData,
  updateTeamMembersCount
} from '../../../redux/slices/subscriptionManagement/subscriptionManagement';
import { useTranslate } from '@tolgee/react';
import PermissionsRadioInputsSectionTwo from './permissionsRadioInputSectiontwo';
import { LomavisCopyableText } from '../../../lomavisSharedUi/atoms/typography/LomavisCopyableText';

const CreateInviteForm = (
  {
    userUpdate = false,
    inviteUpdate = false,
    uuid = null,
    selectedLocations,
    selectedClouds,
    setCockpitRole,
    setCloudRole,
    cockpitAccessType,
    cloudAccessType,
    isOwner = false
  },
  ref
) => {
  const navigate = useNavigate();
  const { data: subscriptionManagementState } = useSelector(
    (state) => state.subscriptionManagement
  );
  const {
    teamMember: {
      email,
      first_name,
      last_name,
      location_access_type,
      cloud_access_type,
      locations_with_access,
      clouds_with_access,
      role,
      cloud_role,
      guest_user_permissions,
      users_permissions
    }
  } = useSelector((state) => state.team);
  const { invite } = useSelector((state) => state.invites);
  const {
    register,
    setError,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    formState: { errors }
  } = useForm();
  useEffect(() => {
    setCockpitRole(watch('role'));
    if (watch('role') === ROLE_GUEST) {
      setValue('cloud_role', null);
    }
  }, [watch('role')]);
  useEffect(() => {
    setCloudRole(watch('cloud_role'));
  }, [watch('cloud_role')]);
  useEffect(() => {
    if ((!email && userUpdate) || (!invite.email && inviteUpdate)) return;
    const permissions = {};
    if (userUpdate) {
      Object.keys(guest_user_permissions).map((permission) => {
        if (guest_user_permissions[permission]) {
          permissions[GUEST_USER_PERMISSIONS[permission].key] = true;
        }
      });
      Object.keys(users_permissions).map((permission) => {
        if (users_permissions[permission]) {
          permissions[ALL_USERS_PERMISSIONS[permission].key] = true;
        }
      });
    }
    if (inviteUpdate) {
      GUEST_USER_PERMISSIONS_KEYS.map((permission) => {
        if (invite[permission]) {
          permissions[permission] = true;
        }
      });
      ALL_USERS_PERMISSIONS_KEYS.map((permission) => {
        if (invite[permission]) {
          permissions[permission] = true;
        }
      });
    }
    reset({
      email: inviteUpdate ? invite.email : email,
      first_name: inviteUpdate ? invite.first_name : first_name,
      last_name: inviteUpdate ? invite.last_name : last_name,
      location_access_type: inviteUpdate
        ? invite.location_access_type
        : location_access_type,
      cloud_access_type: inviteUpdate
        ? invite.cloud_access_type
        : cloud_access_type,
      locations_with_access: selectedLocations,
      clouds_with_access: selectedClouds,
      role: inviteUpdate ? invite.role : role,
      cloud_role: inviteUpdate ? invite.cloud_user_role : cloud_role,
      ...permissions
    });
  }, [
    email,
    first_name,
    last_name,
    location_access_type,
    cloud_access_type,
    locations_with_access,
    clouds_with_access,
    role,
    cloud_role,
    guest_user_permissions,
    users_permissions,
    invite
  ]);

  async function scrollToTopAndShowModal(action) {
    scroll.scrollToTop({
      duration: 300, // You can adjust the time based on how long the scroll usually takes
      smooth: true // Callback function that's called when scrolling is finished
    });
    setTimeout(() => {
      action();
    }, 300);
  }

  useEffect(() => {
    if (!userUpdate && !inviteUpdate) {
      reset({
        email: '',
        first_name: '',
        last_name: '',
        location_access_type: ACCESS_TYPE_ALL,
        cloud_access_type: ACCESS_TYPE_ALL,
        locations_with_access: [],
        clouds_with_access: [],
        role: null,
        cloud_role: null
      });
    }
  }, []);
  const [permissionsError, setPermissionsError] = useState(false);
  const isRadioSelected = () => {
    return Object.keys(GUEST_USER_PERMISSIONS).some((permission) => {
      return watch(GUEST_USER_PERMISSIONS[permission].key);
    });
  };
  const onSubmit = (data) => {
    if (
      subscriptionManagementState?.available != null &&
      subscriptionManagementState?.available <= 0 &&
      !userUpdate &&
      !inviteUpdate
    ) {
      toast.error(t('no_available_users_seats'), {
        position: 'top-right'
      });
      return;
    }

    if (!data.cloud_role && !data.role) {
      scrollToTopAndShowModal(() =>
        toast.error(t('invite.createForm.role_warning'), {
          position: 'top-right'
        })
      );
      return;
    }
    if (selectedLocations.length === 0 && data.role === ROLE_GUEST) {
      scrollToTopAndShowModal(() =>
        toast.error(t('guest_user_must_have_at_least_one_location_selected'), {
          position: 'top-right'
        })
      );
      return;
    }

    if (!isRadioSelected() && data.role === ROLE_GUEST) {
      scrollToTopAndShowModal(() =>
        toast.error(t('at_least_one_permission_should_be_selected'), {
          position: 'top-right'
        })
      );
      return;
    }
    data.location_access_type = data.role
      ? data.role === ROLE_ADMIN
        ? ACCESS_TYPE_ALL
        : cockpitAccessType
      : null;
    data.cloud_access_type = data.cloud_role
      ? data.cloud_role === ROLE_ADMIN
        ? ACCESS_TYPE_ALL
        : cloudAccessType
      : null;
    if (userUpdate) {
      data.locations_with_access = selectedLocations;
      data.clouds_with_access = selectedClouds;
      dispatch(
        updateGuestUser({ uuid, ...data }, errorCallback, updateSuccessCallback)
      );

      return;
    }
    // prepare data for api call
    data.location_uuid_list = selectedLocations;

    data.cloud_uuid_list = selectedClouds;

    if (inviteUpdate) {
      dispatch(
        updateInvite({ uuid, ...data }, errorCallback, updateSuccessCallback)
      );

      return;
    }
    dispatch(createInvite(data, errorCallback, successCallback));
  };
  const errorCallback = (data) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (data?.language) setError('language', { message: data.language[0] });

    if (data?.email) {
      setError('email', { message: data.email[0], type: 'server' });
      scrollToTopAndShowModal(() =>
        toast.error(data.email[0], {
          position: 'top-right'
        })
      );
    }

    if (data?.non_field_errors) {
      scrollToTopAndShowModal(() =>
        toast.error(data.non_field_errors[0], {
          position: 'top-right'
        })
      );
    }
    if (data?.length > 0) {
      scrollToTopAndShowModal(() =>
        toast.error(data[0], {
          position: 'top-right'
        })
      );
    }
  };
  const successCallback = () => {
    if (userUpdate) dispatch(getTeamMemeberDetails(uuid));
    dispatch(fetchSubscriptionData());

    toast.success(t('invite.createForm.success_message'), {
      position: 'top-right'
    });

    setTimeout(() => {
      navigate(PATH_PAGE.team_management);
    }, 1000); // Wait for 3 seconds before navigating to the new page
  };
  const updateSuccessCallback = () => {
    toast.success(t('invite.createForm.updateuccess_message'), {
      position: 'top-right'
    });
    setTimeout(() => {
      navigate(PATH_PAGE.team_management);
    }, 500); // Wait for 3 seconds before navigating to the new page
  };

  // Expose functions or data to the parent component using useImperativeHandle
  useImperativeHandle(ref, () => ({
    submitForm: () => handleSubmit(onSubmit)

    // Add other functions you want to expose
  }));

  const allowEditingCockpitRole = () => {
    if (!hasSocialMediaManagementSubscription) {
      return false;
    }

    if (data?.available_cockpit_users_count == null) {
      return true;
    }

    // form update
    if (userUpdate && role) {
      return true;
    }

    if (inviteUpdate && invite.role) {
      return true;
    }

    return data?.available_cockpit_users_count > 0;
  };

  const allowEditingCloudRole = () => {
    if (!hasCloudSubscription) {
      return false;
    }

    if (data?.available_cloud_users_count == null) {
      return true;
    }

    // form update
    if (userUpdate && cloud_role) {
      return true;
    }

    if (inviteUpdate && invite.cloud_user_role) {
      return true;
    }

    return data?.available_cloud_users_count > 0;
  };

  const { t } = useTranslate();
  const { hasSocialMediaManagementSubscription, hasCloudSubscription } =
    useSelector((state) => state.userDetails);
  const { data } = useSelector((state) => state.subscriptionManagement);
  return (
    <div className="card p-2 rounded">
      <div className="card-body " style={{overflow: "visible"}}>
        {!userUpdate && !inviteUpdate ? (
          <Form.Group className="mb-3 flex-grow-1" controlId="formBasicEmail">
            <Form.Label>{t('invite.createForm.email')}</Form.Label>
            <Form.Control
              type="email"
              placeholder={t('invite.createForm.email')}
              {...register('email', {
                required: t('invite.createForm.invalid_email_format'),
                pattern: {
                  value: /^[a-zA-Z0-9+_.~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t('invite.createForm.invalid_email_format')
                },
                maxLength: {
                  value: 64,
                  message: t('invite.createForm.email_max_length_64')
                }
              })}
              disabled={userUpdate || inviteUpdate}
              isInvalid={errors.email}
            />
            {errors.email && errors.email.type === 'maxLength' && (
              <Form.Control.Feedback type="invalid">
                {t('invite.createForm.email_max_length_64')}
              </Form.Control.Feedback>
            )}
            {errors.email && errors.email.type === 'pattern' && (
              <Form.Control.Feedback type="invalid">
                {t('invite.createForm.invalid_email_format')}
              </Form.Control.Feedback>
            )}

            {errors.email && errors.email.type === 'required' && (
              <Form.Control.Feedback type="invalid">
                {t('invite.createForm.email_required')}
              </Form.Control.Feedback>
            )}
            {errors.email && errors.email.type === 'server' && (
              <Form.Control.Feedback type="invalid">
                {errors.email.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        ) : (
          <Form.Group className="mb-3 flex-grow-1" controlId="formBasicEmail">
            <Form.Label className="fw-bolder">
              {t('invite.createForm.email')}
            </Form.Label>
            <p className="fs-5">{`${userUpdate ? email : invite?.email}`}</p>
          </Form.Group>
        )}
        {inviteUpdate && (
          <div className="mt-3">
            <Form.Group className="mb-3 flex-grow-1" controlId="formBasicInviteUrl">
              <Form.Label className="fw-bolder">
                {t('invite.createForm.invite_url')}
              </Form.Label>
              <LomavisCopyableText 
                text={invite?.invite_url || `${window.location.origin}/invite/${invite?.uuid}`} 
              />
            </Form.Group>
          </div>
        )}
        {userUpdate && (
          <div className="mt-3">
            <Form.Group className="mb-3 flex-grow-1" controlId="formBasicEmail">
              <Form.Label className="fw-bolder">
                {t('invite.createForm.name')}
              </Form.Label>
              <p className="fs-5">{`${first_name} ${last_name}`}</p>
            </Form.Group>
            <Form.Group className="mb-3 flex-grow-1" controlId="formBasicUuid">
              <Form.Label className="fw-bolder">
                {t('invite.createForm.automation_user_id')}
              </Form.Label>
              <LomavisCopyableText 
                text={uuid} 
              />
            </Form.Group>
          </div>
        )}
        <div className="mb-5" />
        {!isOwner && (
          <>
            <p className="form-label fs-5">
              {t('invite.createForm.select_access_type')}
            </p>
            <div className="mb-3" />
          </>
        )}
        {!isOwner && (
          <div className="d-flex gap-3 w-100">
            <Form.Group className="mb-3 w-50" controlId="formBasicEmail">
              <Form.Label>{t('invite.createForm.cockpit_role')}</Form.Label>
            <Controller
              name="role"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown>
                  <Dropdown.Toggle
                    className="w-100"
                    variant="light"
                    id="dropdown-basic"
                    disabled={!allowEditingCockpitRole()}
                  >
                    {value
                      ? t(`config.roles.${roles[value]}_cockpit`)
                      : t('invite.createForm.no_access_cockpit')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {Object.keys(roles).map((id) => (
                      <Dropdown.Item
                        key={id}
                        value={Number(id)}
                        active={value === Number(id)}
                        onClick={() => onChange(Number(id))}
                        className="text-wrap"
                      >
                        <div className="fw-bold d-flex align-items-center text-wrap">
                          {t(`config.roles.${roles[id]}_cockpit`)}
                        </div>
                        <div className="fs-7 text-wrap">
                          {t(`config.roles.${roles[id]}_cockpit_description`)}
                        </div>
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Item
                      value={null}
                      active={value === null}
                      onClick={() => onChange(null)}
                    >
                      <div className="fs-7 text-wrap ">
                        <div className="fw-bold d-flex align-items-center text-wrap">
                          {t('invite.createForm.no_access_cockpit_title')}
                        </div>
                        <div className="fs-7 text-wrap">
                          {t('invite.createForm.no_access_cockpit_description')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            />
            {!hasSocialMediaManagementSubscription && (
              <p className="m-1 text-muted fs-7">
                {t('invite.createForm.cockpit_role_unactive')}
              </p>
            )}
            {hasSocialMediaManagementSubscription &&
              !allowEditingCockpitRole() && (
                <div className="d-flex fs-7">
                  <p className="m-1 text-muted  p-0 m-1">
                    {t('invite.createForm.cockpit_no_user_available')}
                  </p>
                  <a
                    href="/subscription-management/lomavis_cockpit/select/"
                    className="text-primary m-1 p-0 fw-bold"
                  >
                    {t('invite.createForm.upgrade_subscription')}
                  </a>
                </div>
              )}
          </Form.Group>

          <Form.Group className="mb-3 w-50" controlId="formBasicEmail">
            <Form.Label>{t('invite.createForm.cloud_role')}</Form.Label>
            <Controller
              name="cloud_role"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown>
                  <Dropdown.Toggle
                    variant="light"
                    id="dropdown-basic"
                    className="w-100"
                    disabled={
                      watch('role') === ROLE_GUEST || !allowEditingCloudRole()
                    }
                  >
                    {value
                      ? t(`config.roles.${cloudRoles[value]}_cloud`)
                      : t('invite.createForm.no_access_cloud')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {Object.keys(cloudRoles).map((id) => (
                      <Dropdown.Item
                        key={id}
                        value={Number(id)}
                        active={value === Number(id)}
                        onClick={() => onChange(Number(id))}
                        className="text-wrap"
                      >
                        <div className="fw-bold d-flex align-items-center text-wrap">
                          {t(`config.roles.${cloudRoles[id]}_cloud`)}
                        </div>
                        <div className="fs-7 text-wrap">
                          {t(`config.roles.${cloudRoles[id]}_cloud_description`)}
                        </div>
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Item
                      value={null}
                      active={value === null}
                      onClick={() => onChange(null)}
                    >
                      <div className="fw-bold d-flex align-items-center text-wrap">
                        {t('invite.createForm.no_access_cloud_description_title')}
                      </div>
                      <div className="fs-7 text-wrap">
                        {t('invite.createForm.no_access_cloud_description')}
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            />
            {!hasCloudSubscription && (
              <p className="m-1 text-muted fs-7">
                {t('invite.createForm.cloud_role_unactive')}
              </p>
              )}
            {hasCloudSubscription && !allowEditingCloudRole() && (
              <div className="d-flex fs-7">
                <p className="m-1 text-muted  p-0 m-1">
                  {t('invite.createForm.cloud_no_user_available')}
                </p>
                <a
                  href="/subscription-management/lomavis_cloud/select/"
                  className="text-primary m-1 p-0 fw-bold"
                >
                  {t('invite.createForm.upgrade_subscription')}
                </a>
              </div>
            )}
          </Form.Group>
        </div>
        )}
        {!isOwner && watch('role') === ROLE_GUEST ? (
          <div className="d-flex flex-end mb-2">
            <SelectLanguage
              register={register}
              errors={errors}
              control={control}
            />
          </div>
        ) : (
          ''
        )}
        {!isOwner && watch('role') === ROLE_GUEST && (
          <PermissionsRadioInputs
            register={register}
            error={permissionsError}
            setPermissionsError={setPermissionsError}
            watch={watch}
            userUpdate={userUpdate || inviteUpdate}
            uuid={uuid}
          />
        )}
        {!isOwner &&
          watch('role') &&
          (watch('role') === ROLE_PUBLISHER ||
            watch('role') === ROLE_CREATOR) && (
            <PermissionsRadioInputsSectionTwo
              watch={watch}
              register={register}
              error={permissionsError}
              setPermissionsError={setPermissionsError}
              userUpdate={userUpdate || inviteUpdate}
              uuid={uuid}
            />
          )}

        <Toaster />
      </div>
    </div>
  );
};

export default forwardRef(CreateInviteForm);
CreateInviteForm.propTypes = {
  setCockpitRole: PropTypes.any,
  setCloudRole: PropTypes.any,
  selectedLocations: PropTypes.any,
  selectedClouds: PropTypes.any,
  userUpdate: PropTypes.bool,
  inviteUpdate: PropTypes.bool,
  uuid: PropTypes.any,
  cloudAccessType: PropTypes.any,
  cockpitAccessType: PropTypes.any,
  isOwner: PropTypes.bool
};

export const SelectLanguage = ({ register, errors, control }) => {
  const { currentLang, allLangs } = useLocales();
  const { t } = useTranslate();
  const [language, setLanguage] = useState(null);
  useEffect(() => {
    // Set the default language when the component mounts
    setLanguage(currentLang);
  }, [currentLang, allLangs]);

  return (
    <Controller
      name="language"
      control={control}
      defaultValue={currentLang.value}
      render={({ field: { onChange, value } }) => (
        <Dropdown>
          <Dropdown.Toggle variant="light" size="sm" id="dropdown-basic">
            {language ? (
              <>
                <span className="symbol symbol-20px me-3">
                  <img
                    className="rounded-1"
                    src={language.icon}
                    alt={language.label}
                  />
                </span>
                <span className="ml-2">{t(language.label)}</span>
              </>
            ) : (
              t('invite.select_language')
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {allLangs.map((item) => (
              <Dropdown.Item
                key={item.value}
                value={item.value}
                active={value === item.value}
                onClick={() => {
                  onChange(item.value);
                  setLanguage(item);
                }}
              >
                <>
                  <span className="symbol symbol-20px me-3">
                    <img
                      className="rounded-1"
                      src={item.icon}
                      alt={item.label}
                    />
                  </span>
                  <span>{t(item.label)} </span>
                </>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}
    />
  );
};

SelectLanguage.propTypes = {
  register: PropTypes.any,
  errors: PropTypes.any,
  control: PropTypes.any
};
